import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Text } from "rebass/styled-components";
import {
  FiUploadCloud,
  FiX,
  FiMusic,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiRotateCcw,
} from "react-icons/fi";

import { Button } from "MVVM/Components/Button";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  DELETE_USER_SONG,
  RENAME_USER_SONG,
  UPDATE_USER_SONG_TUNING,
  USER_SONGS,
} from "MVVM/GraphQL/userSong";
import { useCustomSongUpload, IUserSong } from "./useCustomSongUpload";
import { useSongAnalysis } from "./useSongAnalysis";
import { BeatVisualizer } from "./BeatVisualizer";
import { MAX_SONG_BYTES } from "MVVM/Utilities/audioValidation";
import * as S from "./styledComponents";

export interface ICustomSongSelection {
  songId: string;
  title: string;
  tempoBpm?: number | null;
}

interface ICustomSongModalProps {
  onClose: () => void;
  onSelect: (song: ICustomSongSelection) => void;
}

const PHASE_STEP = 0.125; // bars (= half a beat)
const PHASE_RANGE = 1; // ±1 bar
const LEAD_MIN = 0;
const LEAD_MAX = 8;

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

// beatPhaseOffset is in bars; render it as a reduced fraction of a bar, or as
// the raw float when it's a custom (non-1/8-step) value
const formatPhase = (bars: number): string => {
  if (bars === 0) return "On the beat";
  const sign = bars > 0 ? "+" : "−";
  const eighths = Math.abs(bars) * 8;
  if (Math.abs(eighths - Math.round(eighths)) > 1e-6)
    return `${sign}${Math.abs(bars)} bar`;
  const e = Math.round(eighths);
  const [num, den] = [e / gcd(e, 8), 8 / gcd(e, 8)];
  const frac = den === 1 ? `${num} bar` : `${num}/${den} bar`;
  return `${sign}${frac}`;
};

const formatDuration = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const songTitle = (song: IUserSong): string =>
  song.title || song.originalFilename || "Untitled";

export const CustomSongModal = ({
  onClose,
  onSelect,
}: ICustomSongModalProps) => {
  const {
    status,
    progress,
    song: uploadedSong,
    error,
    upload,
    reset,
  } = useCustomSongUpload();
  const { data: songsData, refetch } = useQuery(USER_SONGS, {
    fetchPolicy: "cache-and-network",
  });
  const [updateTuning] = useMutation(UPDATE_USER_SONG_TUNING);
  const [renameUserSong] = useMutation(RENAME_USER_SONG);
  const [deleteUserSong] = useMutation(DELETE_USER_SONG);

  // a library-picked song takes over from the (fresh) upload; either drives tuning
  const [existing, setExisting] = useState<IUserSong | null>(null);
  const activeSong = existing ?? uploadedSong;
  const { analysis, error: analysisError } = useSongAnalysis(
    activeSong?.analysisUrl,
  );

  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // a click's target is the common ancestor of mousedown/mouseup, so a drag
  // that starts inside the modal and ends on the backdrop "clicks" the overlay
  // — only close when the press also STARTED on the backdrop itself
  const pressStartedOnOverlayRef = useRef(false);

  // rename/delete controls (shared by the library rows and the tune view)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // manual beat-alignment entry (any float in ±1 bar, finer than the 1/8 steps)
  const [editingPhase, setEditingPhase] = useState(false);
  const [phaseDraft, setPhaseDraft] = useState("");

  // tuning state — seeded from analysis (or the song's saved tuning) per song
  const [chosenDropMs, setChosenDropMs] = useState(0);
  const [beatPhaseOffset, setBeatPhaseOffset] = useState(0);
  const [leadBars, setLeadBars] = useState(2);
  // gates the tuner: the visualizer only mounts once tuning is seeded for this
  // song, so it never renders a frame with the default (drop 0) values
  const [seededSongId, setSeededSongId] = useState<string | null>(null);

  useEffect(() => {
    if (!analysis || !activeSong || seededSongId === activeSong.id) return;
    const tuning = activeSong.tuning;
    setChosenDropMs(tuning?.chosenDropMs ?? analysis.first_beat_ms ?? 0);
    setBeatPhaseOffset(tuning?.beatPhaseOffset ?? 0);
    setLeadBars(tuning?.leadBars ?? 2);
    setSeededSongId(activeSong.id);
  }, [analysis, activeSong, seededSongId]);

  // a freshly analyzed upload should appear in the library on next open
  useEffect(() => {
    if (status === "done") void refetch?.();
  }, [status, refetch]);

  const inUploadFlow =
    status === "validating" || status === "uploading" || status === "analyzing";
  const showTune = !!activeSong && !!analysis && seededSongId === activeSong.id;
  const showLibrary = !activeSong && !inUploadFlow;
  const songs: IUserSong[] = songsData?.userSongs ?? [];
  // renames land in the Apollo cache (the mutation returns the song), so read
  // the active song's display data through the cache-backed list when possible
  const freshActiveSong = activeSong
    ? songs.find((s) => s.id === activeSong.id) ?? activeSong
    : null;

  const handlePickFile = (file?: File | null) => {
    if (file) void upload(file);
  };

  const backToLibrary = () => {
    setExisting(null);
    setSeededSongId(null);
    setEditingId(null);
    setConfirmDeleteId(null);
    reset();
  };

  const startRename = (song: IUserSong) => {
    setConfirmDeleteId(null);
    setTitleDraft(songTitle(song));
    setEditingId(song.id);
  };

  const commitRename = async () => {
    const songId = editingId;
    const title = titleDraft.trim().slice(0, 120);
    setEditingId(null);
    if (!songId || !title) return;
    try {
      await renameUserSong({ variables: { songId, title } });
    } catch {
      // cache keeps the old title — nothing to roll back
    }
  };

  // first click arms the row ("Delete?"), second click deletes
  const handleDelete = async (song: IUserSong) => {
    if (confirmDeleteId !== song.id) {
      setConfirmDeleteId(song.id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteUserSong({ variables: { songId: song.id } });
    } catch {
      return;
    }
    if (activeSong?.id === song.id) backToLibrary();
    void refetch?.();
  };

  const renderTitleEditor = () => (
    <>
      <S.TitleInput
        autoFocus
        maxLength={120}
        value={titleDraft}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTitleDraft(e.target.value)
        }
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") void commitRename();
          if (e.key === "Escape") setEditingId(null);
        }}
      />
      <S.RowActions onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.RowIconButton
          type="button"
          aria-label="Save name"
          onClick={() => void commitRename()}
        >
          <FiCheck size={14} />
        </S.RowIconButton>
      </S.RowActions>
    </>
  );

  const renderRowActions = (song: IUserSong) => (
    <S.RowActions onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <S.RowIconButton
        type="button"
        aria-label="Rename song"
        onClick={() => startRename(song)}
      >
        <FiEdit2 size={13} />
      </S.RowIconButton>
      {confirmDeleteId === song.id ? (
        <S.ConfirmDelete type="button" onClick={() => void handleDelete(song)}>
          Delete?
        </S.ConfirmDelete>
      ) : (
        <S.RowIconButton
          type="button"
          $danger
          aria-label="Delete song"
          onClick={() => void handleDelete(song)}
        >
          <FiTrash2 size={13} />
        </S.RowIconButton>
      )}
    </S.RowActions>
  );

  const resetTuning = () => {
    setChosenDropMs(analysis?.first_beat_ms ?? 0);
    setBeatPhaseOffset(0);
    setLeadBars(2);
    setEditingPhase(false);
  };

  const startPhaseEdit = () => {
    setPhaseDraft(String(beatPhaseOffset));
    setEditingPhase(true);
  };

  const commitPhaseEdit = () => {
    setEditingPhase(false);
    const value = parseFloat(phaseDraft);
    if (Number.isFinite(value))
      setBeatPhaseOffset(Math.max(-PHASE_RANGE, Math.min(PHASE_RANGE, value)));
  };

  const saveTuning = async (): Promise<boolean> => {
    if (!activeSong) return false;
    setSaving(true);
    setSaveError(null);
    try {
      await updateTuning({
        variables: {
          songId: activeSong.id,
          // no mixWeight — music volume comes from the main fragmovie settings
          // (timeRemapParams.amsMixWeight wins over tuning.mixWeight in pnd)
          tuning: {
            chosenDropMs: Math.round(chosenDropMs),
            beatPhaseOffset,
            leadBars,
          },
        },
      });
      return true;
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not save tuning");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUse = async () => {
    if (!activeSong || !(await saveTuning())) return;
    onSelect({
      songId: activeSong.id,
      title: songTitle(freshActiveSong ?? activeSong),
      tempoBpm: activeSong.tempoBpm,
    });
    onClose();
  };

  return (
    <S.Overlay
      onMouseDown={(e: React.MouseEvent) => {
        pressStartedOnOverlayRef.current = e.target === e.currentTarget;
      }}
      onClick={(e: React.MouseEvent) => {
        if (pressStartedOnOverlayRef.current && e.target === e.currentTarget)
          onClose();
      }}
    >
      <S.Container onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.Header>
          <Text variant="text.headingXSmall">
            {showTune ? "Fine-tune your song" : "Custom song"}
          </Text>
          <S.CloseButton onClick={onClose} type="button" aria-label="Close">
            <FiX size={18} />
          </S.CloseButton>
        </S.Header>

        <S.Body>
          {/* ── Library: pick an existing song or upload a new one ── */}
          {showLibrary && (
            <>
              <S.CompactDrop
                $active={dragActive}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e: React.DragEvent) => {
                  e.preventDefault();
                  setDragActive(false);
                  handlePickFile(e.dataTransfer.files?.[0]);
                }}
              >
                <FiUploadCloud size={18} />
                Upload a new song
              </S.CompactDrop>
              <S.HintText>
                mp3, wav, m4a/aac, ogg or flac · up to{" "}
                {MAX_SONG_BYTES / (1024 * 1024)} MB
              </S.HintText>
              <S.HiddenInput
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePickFile(e.target.files?.[0])
                }
              />
              {status === "error" && error && (
                <S.ErrorText>{error}</S.ErrorText>
              )}

              <S.SectionLabel>Your songs</S.SectionLabel>
              {songs.length === 0 ? (
                <S.EmptyLibrary>
                  No uploads yet — add one above to get started.
                </S.EmptyLibrary>
              ) : (
                <S.LibraryList>
                  {songs.map((s) => {
                    const done = s.status === "DONE";
                    return (
                      <S.LibraryRow
                        key={s.id}
                        $disabled={!done}
                        onClick={() => {
                          if (!done || editingId === s.id) return;
                          setConfirmDeleteId(null);
                          setExisting(s);
                        }}
                      >
                        <FiMusic size={14} />
                        {editingId === s.id ? (
                          renderTitleEditor()
                        ) : (
                          <>
                            <S.LibraryRowMain>
                              <S.LibrarySongName>
                                {songTitle(s)}
                              </S.LibrarySongName>
                              <S.LibraryMeta>
                                {[
                                  s.tempoBpm
                                    ? `${Math.round(s.tempoBpm)} BPM`
                                    : null,
                                  s.durationMs
                                    ? formatDuration(s.durationMs)
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </S.LibraryMeta>
                            </S.LibraryRowMain>
                            {!done && (
                              <S.LibraryStatusTag
                                $variant={
                                  s.status === "ERROR" ? "error" : "pending"
                                }
                              >
                                {s.status === "ERROR" ? "Error" : "Analyzing…"}
                              </S.LibraryStatusTag>
                            )}
                            {renderRowActions(s)}
                          </>
                        )}
                      </S.LibraryRow>
                    );
                  })}
                </S.LibraryList>
              )}
            </>
          )}

          {status === "validating" && (
            <S.CenteredColumn>
              <LoadingSpinner height={30} width={30} />
              <S.StatusText>Checking your file…</S.StatusText>
            </S.CenteredColumn>
          )}

          {status === "uploading" && (
            <S.CenteredColumn>
              <S.StatusText>Uploading… {progress}%</S.StatusText>
              <S.ProgressTrack>
                <S.ProgressFill $pct={progress} />
              </S.ProgressTrack>
            </S.CenteredColumn>
          )}

          {status === "analyzing" && (
            <S.CenteredColumn>
              <LoadingSpinner height={30} width={30} />
              <S.StatusText>
                Analyzing beat grid &amp; drops… this takes a few seconds.
              </S.StatusText>
            </S.CenteredColumn>
          )}

          {activeSong && !showTune && !analysisError && (
            <S.CenteredColumn>
              <LoadingSpinner height={30} width={30} />
              <S.StatusText>Loading analysis…</S.StatusText>
            </S.CenteredColumn>
          )}

          {activeSong && analysisError && (
            <S.ErrorText>{analysisError}</S.ErrorText>
          )}

          {/* ── Fine-tune ── */}
          {showTune && analysis && activeSong?.previewUrl && (
            <>
              <S.SongTitleRow>
                <FiMusic size={14} />
                {editingId === activeSong.id ? (
                  renderTitleEditor()
                ) : (
                  <>
                    <S.SongTitleText>
                      {songTitle(freshActiveSong ?? activeSong)}
                    </S.SongTitleText>
                    {renderRowActions(activeSong)}
                  </>
                )}
              </S.SongTitleRow>

              <BeatVisualizer
                previewUrl={activeSong.previewUrl}
                analysis={analysis}
                chosenDropMs={chosenDropMs}
                beatPhaseOffset={beatPhaseOffset}
                leadBars={leadBars}
                onChooseDrop={setChosenDropMs}
              />

              <S.ControlGroup>
                <S.ControlLabelRow>
                  <S.RowActions>
                    <S.ControlLabel>Timing adjustment</S.ControlLabel>
                    <S.RowIconButton
                      type="button"
                      aria-label="Enter manual value"
                      title="Enter manual value"
                      onClick={startPhaseEdit}
                    >
                      <FiEdit2 size={12} />
                    </S.RowIconButton>
                    <S.RowIconButton
                      type="button"
                      aria-label="Reset timing adjustment"
                      title="Reset to on the beat"
                      onClick={() => {
                        setBeatPhaseOffset(0);
                        setEditingPhase(false);
                      }}
                    >
                      <FiRotateCcw size={12} />
                    </S.RowIconButton>
                    <S.InfoTip>
                      <S.InfoTipIcon
                        type="button"
                        aria-label="About beat alignment"
                      >
                        ?
                      </S.InfoTipIcon>
                      <S.InfoTipBubble $align="left">
                        Shifts the beats relative to the <b>Events</b> (kills
                        and other clip moments) by up to ±1 bar. Use <b>+/−</b>{" "}
                        for 1/8-bar steps, or the pencil to enter an exact
                        value. <b>+</b> = earlier, <b>−</b> = later.
                      </S.InfoTipBubble>
                    </S.InfoTip>
                  </S.RowActions>
                </S.ControlLabelRow>
                {editingPhase ? (
                  <S.PhaseEditRow>
                    <S.PhaseInput
                      autoFocus
                      type="number"
                      min={-PHASE_RANGE}
                      max={PHASE_RANGE}
                      step={0.005}
                      value={phaseDraft}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPhaseDraft(e.target.value)
                      }
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") commitPhaseEdit();
                        if (e.key === "Escape") setEditingPhase(false);
                      }}
                    />
                    <S.RowIconButton
                      type="button"
                      aria-label="Apply"
                      title="Apply"
                      onClick={commitPhaseEdit}
                    >
                      <FiCheck size={14} />
                    </S.RowIconButton>
                    <S.RowIconButton
                      type="button"
                      aria-label="Cancel"
                      title="Cancel"
                      onClick={() => setEditingPhase(false)}
                    >
                      <FiX size={14} />
                    </S.RowIconButton>
                  </S.PhaseEditRow>
                ) : (
                  <S.Stepper>
                    <S.StepButton
                      type="button"
                      disabled={beatPhaseOffset <= -PHASE_RANGE}
                      onClick={() =>
                        setBeatPhaseOffset((v) =>
                          Math.max(-PHASE_RANGE, +(v - PHASE_STEP).toFixed(3)),
                        )
                      }
                    >
                      −
                    </S.StepButton>
                    <S.StepValue>{formatPhase(beatPhaseOffset)}</S.StepValue>
                    <S.StepButton
                      type="button"
                      disabled={beatPhaseOffset >= PHASE_RANGE}
                      onClick={() =>
                        setBeatPhaseOffset((v) =>
                          Math.min(PHASE_RANGE, +(v + PHASE_STEP).toFixed(3)),
                        )
                      }
                    >
                      +
                    </S.StepButton>
                  </S.Stepper>
                )}
              </S.ControlGroup>

              <S.ControlGroup>
                <S.ControlLabelRow>
                  <S.ControlLabel>Lead up</S.ControlLabel>
                </S.ControlLabelRow>
                <S.Stepper>
                  <S.StepButton
                    type="button"
                    disabled={leadBars <= LEAD_MIN}
                    onClick={() =>
                      setLeadBars((v) => Math.max(LEAD_MIN, v - 1))
                    }
                  >
                    −
                  </S.StepButton>
                  <S.StepValue>
                    {leadBars} {leadBars === 1 ? "bar" : "bars"}
                  </S.StepValue>
                  <S.StepButton
                    type="button"
                    disabled={leadBars >= LEAD_MAX}
                    onClick={() =>
                      setLeadBars((v) => Math.min(LEAD_MAX, v + 1))
                    }
                  >
                    +
                  </S.StepButton>
                </S.Stepper>
              </S.ControlGroup>

              <S.ResetLink type="button" onClick={resetTuning}>
                Reset all
              </S.ResetLink>

              {saveError && <S.ErrorText>{saveError}</S.ErrorText>}
            </>
          )}
        </S.Body>

        <S.Footer>
          <Button
            classification="roundedFilledAlt"
            size="small"
            onClick={activeSong ? backToLibrary : onClose}
            flex={true}
          >
            Back
          </Button>
          {showTune && (
            <S.FooterActions>
              <Button
                classification="roundedFilledAlt"
                size="small"
                onClick={() => void saveTuning()}
                disabled={saving}
                flex={true}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                classification="roundedFilled"
                size="small"
                onClick={handleUse}
                disabled={saving}
                flex={true}
              >
                Use this song
              </Button>
            </S.FooterActions>
          )}
        </S.Footer>
      </S.Container>
    </S.Overlay>
  );
};
